import React, { useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { COUNTRIES, CountryType } from '../constants/Countries';

interface CountrySelectProps {
  value?: string | null;
  onChange: (country: string | null) => void;
  onBlur?: () => void;
  placeholder?: string;
  showFlags?: boolean;
  disabled?: boolean;
  className?: string;
}

export const CountrySelect: React.FC<CountrySelectProps> = ({
  value,
  onChange,
  onBlur,
  placeholder = 'Select a country',
  showFlags = false,
  disabled = false,
  className,
}) => {
  const [open, setOpen] = useState(false);

  const selectedCountry = value ? COUNTRIES.find(country => country.label === value) || null : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between", className)}
          disabled={disabled}
          onBlur={onBlur}
        >
          <div className="flex items-center">
            {selectedCountry && showFlags && (
              <img
                loading="lazy"
                width="20"
                height="15"
                srcSet={`https://flagcdn.com/w40/${selectedCountry.code.toLowerCase()}.png 2x`}
                src={`https://flagcdn.com/w20/${selectedCountry.code.toLowerCase()}.png`}
                alt=""
                className="mr-2 rounded-sm"
              />
            )}
            {selectedCountry ? selectedCountry.label : placeholder}
          </div>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0">
        <Command>
          <CommandInput placeholder="Search countries..." className="h-9" />
          <CommandList>
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              {COUNTRIES.map((country) => (
                <CommandItem
                  key={country.code}
                  value={country.label}
                  onSelect={() => {
                    onChange(country.label === value ? null : country.label);
                    setOpen(false);
                  }}
                >
                  <div className="flex items-center">
                    {showFlags && (
                      <img
                        loading="lazy"
                        width="20"
                        height="15"
                        srcSet={`https://flagcdn.com/w40/${country.code.toLowerCase()}.png 2x`}
                        src={`https://flagcdn.com/w20/${country.code.toLowerCase()}.png`}
                        alt=""
                        className="mr-2 rounded-sm"
                      />
                    )}
                    {country.label}
                  </div>
                  <Check
                    className={cn(
                      "ml-auto h-4 w-4",
                      value === country.label ? "opacity-100" : "opacity-0"
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};